/**
 * SAAS-2 · D · 「今日」模块左侧二级细栏（仅订阅版 + 顶部导航版式；企业版 / 侧栏版式不出）。
 * 契约：designs/mockups/theme/个人站订阅版-样稿.html ① 左侧（tag saas-mockup-v1）：经营助手 / 今日出团 / 待确认 N / 付款待初筛 N / 交接 · 交接墙。
 *
 * 🔴 数字 N 只读 SAAS-1 的待办接口 GET /api/v1/assistant/todos（同一个 queryKey，与首页格子共用同一份缓存 = 同一个数），不另写查询；
 *    接口里没有这一格（没权限 / 没数据来源）= 细栏不出这一项。其余各项按目标页的权限出（与侧栏同一套 NAV_PERMS / useCan）。
 */
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useCan, useCanAny } from "@/auth/permissions";
import { NAV_PERMS } from "@/auth/routePermissions";
import { assistantApi, ASSISTANT_QK, type AssistantTodoItem } from "@/services/assistant";
import { pacificToday } from "@/pages/dispatch/boardDisplay";

const item = "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50";
const on = "bg-brand-soft font-semibold text-brand";

export function TodaySubnav() {
  const loc = useLocation();
  const canBoard = useCan("dispatch.center.view");
  const canHandoff = useCanAny(NAV_PERMS.handoff ?? ["handover.items.view"]);
  const todos = useQuery({ queryKey: [...ASSISTANT_QK, "todos"], queryFn: assistantApi.todos, refetchInterval: 120_000 });
  const byKey = new Map<string, AssistantTodoItem>((todos.data?.groups ?? []).flatMap((g) => g.items).map((i) => [i.key, i]));
  const counted = (["orders_pending_confirm", "payment_screen"] as const).map((k) => byKey.get(k)).filter((x): x is AssistantTodoItem => !!x);
  const shortLabel: Record<string, string> = { orders_pending_confirm: "待确认", payment_screen: "付款待初筛" };
  const here = (to: string) => loc.pathname + loc.search === to;

  return (
    <nav className="hidden w-44 shrink-0 flex-col gap-0.5 border-r border-slate-200 bg-white p-2 md:flex" data-testid="today-subnav" aria-label="今日">
      <NavLink to="/" end className={({ isActive }) => cn(item, isActive && on)}>经营助手</NavLink>
      {canBoard && <NavLink to={`/dispatch/board?from=${pacificToday()}`} className={cn(item)} data-testid="today-sub-board">今日出团</NavLink>}
      {counted.map((t) => (
        <NavLink key={t.key} to={t.link} className={cn(item, here(t.link) && on)} data-testid={`today-sub-${t.key}`}>
          <span>{shortLabel[t.key] ?? t.label}</span>
          <em className="text-[11px] not-italic text-slate-400" data-testid={`today-sub-count-${t.key}`}>{t.count}</em>
        </NavLink>
      ))}
      {canHandoff && (
        <>
          <div className="px-2.5 pb-0.5 pt-2.5 text-[11px] text-slate-400">交接</div>
          <NavLink to="/ops/handoff" className={({ isActive }) => cn(item, isActive && on)}>交接墙</NavLink>
        </>
      )}
    </nav>
  );
}
